// Tag management functions
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  query,
  where,
  orderBy,
  serverTimestamp,
  writeBatch,
  onSnapshot
} from 'firebase/firestore'
import { db } from './config'

const TAGS_COLLECTION = 'tags'
const CONTACTS_COLLECTION = 'contacts'

/**
 * Create a new tag
 * @param {string} tagName - Tag name
 * @param {string} userId - Current user ID
 * @returns {Promise<string>} Document ID
 */
export const createTag = async (tagName, userId) => {
  try {
    // Check if tag already exists for this user
    const existingTags = await getTags(userId)
    const normalizedTagName = tagName.trim()
    
    if (existingTags.some(tag => tag.name.toLowerCase() === normalizedTagName.toLowerCase())) {
      throw new Error('Tag already exists')
    }

    const docRef = await addDoc(collection(db, TAGS_COLLECTION), {
      name: normalizedTagName,
      owner: userId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    })
    return docRef.id
  } catch (error) {
    console.error('Error creating tag:', error)
    throw error
  }
}

/**
 * Get all tags for a user
 * @param {string} userId - User ID
 * @returns {Promise<Array>} Array of tags
 */
export const getTags = async (userId) => {
  try {
    const q = query(
      collection(db, TAGS_COLLECTION),
      where('owner', '==', userId),
      orderBy('name', 'asc')
    )
    
    const querySnapshot = await getDocs(q)
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }))
  } catch (error) {
    console.error('Error getting tags:', error)
    throw error
  }
}

/**
 * Subscribe to tags changes (real-time)
 * @param {string} userId - User ID
 * @param {Function} callback - Callback function
 * @returns {Function} Unsubscribe function
 */
export const subscribeToTags = (userId, callback) => {
  const q = query(
    collection(db, TAGS_COLLECTION),
    where('owner', '==', userId),
    orderBy('name', 'asc')
  )
  
  return onSnapshot(q, (snapshot) => {
    const tags = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }))
    callback(tags)
  }, (error) => {
    console.error('Error subscribing to tags:', error)
  })
}

/**
 * Update a tag name
 * @param {string} tagId - Tag document ID
 * @param {string} newName - New tag name
 * @param {string} userId - User ID
 * @returns {Promise<void>}
 */
export const updateTag = async (tagId, newName, userId) => {
  try {
    const normalizedNewName = newName.trim()
    
    // Check if new name already exists
    const existingTags = await getTags(userId)
    const existingTag = existingTags.find(tag => 
      tag.id !== tagId && tag.name.toLowerCase() === normalizedNewName.toLowerCase()
    )
    
    if (existingTag) {
      throw new Error('A tag with this name already exists')
    }

    // Update tag document
    const tagRef = doc(db, TAGS_COLLECTION, tagId)
    await updateDoc(tagRef, {
      name: normalizedNewName,
      updatedAt: serverTimestamp()
    })

    // Update all contacts that use this tag
    const oldTag = existingTags.find(tag => tag.id === tagId)
    if (oldTag) {
      await renameTagInContacts(oldTag.name, normalizedNewName, userId)
    }
  } catch (error) {
    console.error('Error updating tag:', error)
    throw error
  }
}

/**
 * Rename a tag in all contacts that use it
 * @param {string} oldTagName - Old tag name
 * @param {string} newTagName - New tag name
 * @param {string} userId - User ID
 */
const renameTagInContacts = async (oldTagName, newTagName, userId) => {
  try {
    // Get all contacts with this tag
    const contactsQuery = query(
      collection(db, CONTACTS_COLLECTION),
      where('owner', '==', userId)
    )
    
    const contactsSnapshot = await getDocs(contactsQuery)
    const batch = writeBatch(db)
    let updateCount = 0

    contactsSnapshot.docs.forEach((contactDoc) => {
      const contactData = contactDoc.data()
      const tags = contactData.tags || []
      
      if (tags.includes(oldTagName)) {
        const updatedTags = tags.map(tag => tag === oldTagName ? newTagName : tag)
        batch.update(contactDoc.ref, { tags: updatedTags })
        updateCount++
      }
    })

    if (updateCount > 0) {
      await batch.commit()
    }
  } catch (error) {
    console.error('Error renaming tag in contacts:', error)
    throw error
  }
}

/**
 * Delete a tag
 * @param {string} tagId - Tag document ID
 * @param {string} userId - User ID
 * @param {boolean} removeFromContacts - Whether to remove tag from all contacts
 * @returns {Promise<void>}
 */
export const deleteTag = async (tagId, userId, removeFromContacts = false) => {
  try {
    // Get tag name before deleting
    const tags = await getTags(userId)
    const tag = tags.find(t => t.id === tagId)
    
    if (!tag) {
      throw new Error('Tag not found')
    }

    // Delete tag document
    const tagRef = doc(db, TAGS_COLLECTION, tagId)
    await deleteDoc(tagRef)

    // Optionally remove tag from all contacts
    if (removeFromContacts) {
      await removeTagFromContacts(tag.name, userId)
    }
  } catch (error) {
    console.error('Error deleting tag:', error)
    throw error
  }
}

/**
 * Remove a tag from all contacts
 * @param {string} tagName - Tag name to remove
 * @param {string} userId - User ID
 */
const removeTagFromContacts = async (tagName, userId) => {
  try {
    const contactsQuery = query(
      collection(db, CONTACTS_COLLECTION),
      where('owner', '==', userId)
    )
    
    const contactsSnapshot = await getDocs(contactsQuery)
    const batch = writeBatch(db)
    let updateCount = 0

    contactsSnapshot.docs.forEach((contactDoc) => {
      const contactData = contactDoc.data()
      const tags = contactData.tags || []
      
      if (tags.includes(tagName)) {
        const updatedTags = tags.filter(tag => tag !== tagName)
        batch.update(contactDoc.ref, { tags: updatedTags })
        updateCount++
      }
    })

    if (updateCount > 0) {
      await batch.commit()
    }
  } catch (error) {
    console.error('Error removing tag from contacts:', error)
    throw error
  }
}

/**
 * Get usage count for a tag (how many contacts use it)
 * @param {string} tagName - Tag name
 * @param {string} userId - User ID
 * @returns {Promise<number>} Number of contacts using this tag
 */
export const getTagUsageCount = async (tagName, userId) => {
  try {
    const contactsQuery = query(
      collection(db, CONTACTS_COLLECTION),
      where('owner', '==', userId)
    )
    
    const contactsSnapshot = await getDocs(contactsQuery)
    let count = 0

    contactsSnapshot.docs.forEach((contactDoc) => {
      const tags = contactDoc.data().tags || []
      if (tags.includes(tagName)) {
        count++
      }
    })

    return count
  } catch (error) {
    console.error('Error getting tag usage count:', error)
    return 0
  }
}

